import clsx from 'clsx';
import { AlertCircle, AlertTriangle, Info, X, XCircle } from 'lucide-react';

/**
 * Canonical error / warning / info banner used in-context (inside forms,
 * cards, and tabs). Use this for persistent error states the user needs to
 * see while they decide what to do — for transient feedback after an action,
 * use the toast helper in lib/toast.js instead.
 *
 *   <ErrorBanner
 *     title="Audit failed"
 *     body="Quota exceeded — upgrade your plan to run more audits this month."
 *     action={<button className="btn-secondary">Upgrade</button>}
 *     onDismiss={() => setError(null)}
 *   />
 *
 * Variants:
 *   - error (default): red palette, AlertCircle
 *   - warn: amber palette, AlertTriangle
 *   - info: brand palette, Info
 */
const VARIANTS = {
  error: {
    icon: XCircle,
    wrap: 'bg-rose-50 border-rose-200 text-rose-800',
    title: 'text-rose-900',
    iconCls: 'text-rose-600',
  },
  warn: {
    icon: AlertTriangle,
    wrap: 'bg-amber-50 border-amber-200 text-amber-900',
    title: 'text-amber-900',
    iconCls: 'text-amber-600',
  },
  info: {
    icon: Info,
    wrap: 'bg-brand-50 border-brand-100 text-brand-800',
    title: 'text-brand-900',
    iconCls: 'text-brand-600',
  },
};

export default function ErrorBanner({
  title,
  body,
  variant = 'error',
  action,
  onDismiss,
  icon: CustomIcon,
  className = '',
}) {
  const v = VARIANTS[variant] || VARIANTS.error;
  const Icon = CustomIcon || v.icon;

  // Body is what's always shown; title is optional and only renders if non-empty.
  const hasTitle = title && String(title).trim().length > 0;

  return (
    <div
      role={variant === 'error' ? 'alert' : 'status'}
      className={clsx(
        'rounded-xl border text-sm p-4 flex items-start gap-3',
        v.wrap,
        className,
      )}
    >
      <Icon className={clsx('w-5 h-5 flex-none mt-0.5', v.iconCls)} strokeWidth={2} />
      <div className="flex-1 min-w-0">
        {hasTitle && (
          <div className={clsx('font-semibold mb-0.5', v.title)}>{title}</div>
        )}
        <div className="leading-relaxed">{body}</div>
        {action && <div className="mt-3">{action}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="flex-none text-current/60 hover:text-current rounded-md p-0.5 hover:bg-black/5 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}

// Convenience exports for non-error variants, in case the caller prefers
// the explicit name over the variant prop.
export function WarnBanner(props) {
  return <ErrorBanner {...props} variant="warn" />;
}
export function InfoBanner(props) {
  return <ErrorBanner {...props} variant="info" />;
}
